import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, Grid3X3, List, Plus, Grid, Search, RefreshCw } from 'lucide-react';
import { NotificationCenter } from './notifications/NotificationCenter';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { useSearchContext } from '@/components/search/SearchProvider';
import { useGlobalHotkeys } from '@/hooks/useGlobalHotkeys';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { VoiceInputButton } from '@/components/voice/VoiceInputButton';
import { AdminPresenceIndicator } from '@/components/admin/AdminPresenceIndicator';
import T from '@/components/T';
import { dashboardTranslations } from '@/i18n/locales/components/dashboard';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface TopBarProps {
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
}

export const TopBar = ({ onToggleSidebar, sidebarCollapsed }: TopBarProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess, isExpired, isAdmin } = useSubscriptionAccess();
  const { setIsCommandPaletteOpen } = useSearchContext();
  const queryClient = useQueryClient();

  useGlobalHotkeys({
    onCommandK: () => setIsCommandPaletteOpen(true),
    onEscape: () => setIsCommandPaletteOpen(false),
  });
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const isOnBookingsList = location.pathname === '/admin/bookings-list';
  const isOnRoomGrid = location.pathname === '/admin/room-grid' || location.pathname === '/admin/room-grid-lite';
  // Читаем текущий режим из localStorage
  const [currentView, setCurrentView] = useState<'cards' | 'table'>(() => {
    try {
      const saved = localStorage.getItem('hotel3d_bookings_settings');
      if (saved) {
        return JSON.parse(saved).defaultView || 'cards';
      }
    } catch {}
    return 'cards';
  });
  
  // Слушаем изменения из компонента списка
  useEffect(() => {
    const handleViewChange = (e: CustomEvent<{ view: 'cards' | 'table' }>) => {
      setCurrentView(e.detail.view);
    };
    window.addEventListener('bookings-view-changed', handleViewChange as EventListener);
    return () => window.removeEventListener('bookings-view-changed', handleViewChange as EventListener);
  }, []);
  
  const handleBookingsClick = () => {
    if (isOnBookingsList) {
      // Переключаем режим вида
      const newView = currentView === 'cards' ? 'table' : 'cards';
      setCurrentView(newView);
      
      // Сохраняем в localStorage
      try {
        const saved = localStorage.getItem('hotel3d_bookings_settings');
        const settings = saved ? JSON.parse(saved) : {};
        settings.defaultView = newView;
        localStorage.setItem('hotel3d_bookings_settings', JSON.stringify(settings));
        
        // Оповещаем компонент списка об изменении
        window.dispatchEvent(new CustomEvent('bookings-view-changed', { 
          detail: { view: newView } 
        }));
      } catch {}
    } else {
      navigate('/admin/bookings-list');
    }
  };

  // 🔫 АК-47: Очистка кэша ПЕРЕД переходом на шахматку
  const handleGridClick = () => {
    console.log('🔫 [AK-47] TopBar: Clearing cache before grid navigation');
    queryClient.clear();
    navigate(isMobile ? '/admin/room-grid-lite' : '/admin/room-grid');
  };

  const handleNewBooking = () => {
    window.dispatchEvent(new Event('open-quick-booking'));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries();
    setLastRefreshTime(new Date());
    setTimeout(() => setIsRefreshing(false), 500);
  };

  return (
    <header className="flex items-center justify-between h-16 px-4 border-b border-border bg-background">
      <div className="flex items-center gap-2">
        {/* Menu button removed - already rendered in MobileOptimizedLayout */}
        
        {/* Mobile navigation cluster */}
        {isMobile && hasAccess && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleGridClick}
              className="h-8 w-8"
              title="Шахматка"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={isOnBookingsList ? "default" : "ghost"}
              size="icon"
              onClick={handleBookingsClick}
              className="h-8 w-8"
              title={isOnBookingsList ? (currentView === 'cards' ? 'Переключить на таблицу' : 'Переключить на карточки') : 'Заказы'}
            >
              {/* На мобилке иконка должна быть стабильной ("Заказы"), а не зависеть от режима карточки/таблица */}
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant="default"
              size="icon"
              onClick={handleNewBooking}
              className="h-8 w-8"
              title="Новая бронь"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className="h-8 w-8"
              title="Обновить"
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            </Button>
          </div>
        )}
        
        {/* Desktop menu button */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleSidebar}
            className="p-2"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        
        {/* Navigation buttons next to menu */}
        {!isMobile && hasAccess && (
          <>
            <div className="flex items-center gap-1 bg-background rounded-lg p-1 border ml-2">
              <Button
                variant={isOnRoomGrid ? "default" : "ghost"}
                size="sm"
                onClick={handleGridClick}
                className={cn(
                  "h-9 px-3 gap-2",
                  isOnRoomGrid && "bg-primary hover:bg-primary/90"
                )}
              >
                <Grid3X3 className="h-4 w-4" />
                <span className="flex flex-col items-start leading-tight">
                  <span>Шахматка</span>
                  <span className="text-[10px] opacity-70 -mt-0.5">Сетка</span>
                </span>
              </Button>
              <Button
                variant={isOnBookingsList ? "default" : "ghost"}
                size="sm"
                onClick={handleBookingsClick}
                className={cn(
                  "h-9 px-3 gap-2",
                  isOnBookingsList && "bg-primary hover:bg-primary/90"
                )}
                title={isOnBookingsList ? 'Переключить режим отображения' : 'Режим списка бронирований'}
              >
                {currentView === 'cards' ? (
                  <Grid className="h-4 w-4" />
                ) : (
                  <List className="h-4 w-4" />
                )}
                <span className="flex flex-col items-start leading-tight">
                  <span>Заказы</span>
                  <span className="text-[10px] opacity-70 -mt-0.5">
                    {currentView === 'cards' ? 'Карточки' : 'Список'}
                  </span>
                </span>
              </Button>
            </div>
            <Button
              size="sm"
              onClick={handleNewBooking}
              className="h-8 px-3 gap-2 ml-1"
            >
              <Plus className="h-4 w-4" />
              <span>Бронь</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="h-8 px-2 gap-1.5 ml-1"
              title="Обновить данные"
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
              <span className="text-xs">{format(lastRefreshTime, 'HH:mm')}</span>
            </Button>
          </>
        )}
      </div>
      
      {/* Spacer with subscription warning */}
      <div className="flex-1 flex items-center justify-center">
        {isExpired && isAdmin && (
          <div className="text-destructive font-medium text-sm">
            <T ru={dashboardTranslations.ru.topBar.subscriptionExpired} en={dashboardTranslations.en.topBar.subscriptionExpired} />
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {/* Mobile search button */}
        {isMobile && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => { console.log('[search-mobile] open'); setIsCommandPaletteOpen(true); }}
            className="h-9 w-9 active:scale-95 transition-transform"
            aria-label="Поиск"
            title="Поиск"
          >
            <Search className="h-5 w-5" />
          </Button>
        )}
        
        {!isMobile && <AdminPresenceIndicator showBadge={true} maxAvatars={4} />}
        {!isMobile && <LanguageSwitcher variant="ghost" size="sm" showText={!isMobile} />}
        <ThemeToggle />
        {!isMobile && <VoiceInputButton />}
        {!isMobile && <GlobalSearch />}
        <NotificationCenter />
      </div>
    </header>
  );
};
